import { deriveFilterTag, FilterTag } from "./deriveFilterTag";

/**
 * Extracts `bits` and returns a masked key string for deduplication.
 */
function filterKey(tag: FilterTag): string {
  const bits = tag[0] >> 2; // first 6 bits = bits
  const out = new Uint8Array(6);
  let bitCursor = 6;

  for (let i = 0; i < 46 - 6; i++) {
    const bitIndex = bitCursor + i;
    const byteIndex = Math.floor(bitIndex / 8);
    const bitInByte = 7 - (bitIndex % 8);
    const value = (tag[byteIndex] >> bitInByte) & 1;

    const outIndex = Math.floor(i / 8);
    const outBit = 7 - (i % 8);
    out[outIndex] |= value << outBit;
  }

  return Buffer.from(out).toString("hex");
}

/**
 * Generates a deduplicated list of unlinkable packed filter tags from a public view key.
 * Returns up to `count` unique tags based on the number of available `bits` (max 2^bits).
 *
 * @param viewKey 32-byte public view key
 * @param bits Number of bits to use per tag (1–40)
 * @param count Max number of tags to return (default: 1024)
 * @returns Array of unique 6-byte FilterTag buffers
 */
export function deriveFilterTags({
  viewKey,
  bits,
  count = 1024,
}: {
  viewKey: Uint8Array;
  bits: number;
  count?: number;
}): FilterTag[] {
  const maxTags = 2 ** bits;
  const limit = Math.min(count, maxTags);

  const seen = new Set<string>();
  const tags: FilterTag[] = [];

  let index = 0;
  while (tags.length < limit) {
    const tag = deriveFilterTag({ viewKey, index, bits });
    const key = filterKey(tag);
    if (!seen.has(key)) {
      seen.add(key);
      tags.push(tag);
    }
    index++;
  }

  return tags;
}